import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { 
  CreditCardIcon, 
  LockClosedIcon,
  CheckIcon,
  ArrowLeftIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [squareEnvironment, setSquareEnvironment] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  const paymentsRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (location.state?.cart) {
      setCart(location.state.cart);
      setCardholderName(`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '');
    } else {
      // Redirect to home if no cart data
      navigate('/');
    }
  }, [location.state, navigate, user]);

  // Load Square Web Payments SDK and get application ID
  useEffect(() => {
    const loadSquareSDK = async () => {
      try {
        // Get Square Application ID and environment from server
        const response = await api.get('/api/square/application-id');
        
        if (!response.data.applicationId) {
          throw new Error('Square Application ID not received from server');
        }
        
        setApplicationId(response.data.applicationId);
        setSquareEnvironment(response.data.environment || 'sandbox'); // Default to sandbox if not specified

        // Load Square Web Payments SDK script
        if (!window.Square) {
          const script = document.createElement('script');
          // Use the environment from server to load the correct SDK URL
          const environment = response.data.environment || 'sandbox';
          script.src = environment === 'production'
            ? 'https://web.squarecdn.com/v1/square.js'
            : 'https://sandbox.web.squarecdn.com/v1/square.js';
          script.async = true;
          script.onload = () => {
            setSquareLoaded(true);
          };
          script.onerror = () => {
            toast.error('Failed to load Square payment system. Please refresh the page.');
          };
          document.body.appendChild(script);
        } else {
          setSquareLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load Square SDK:', error);
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            'Square Application ID is not configured. Please contact support.';
        toast.error(errorMessage, { duration: 6000 });
      }
    };

    if (cart) {
      loadSquareSDK();
    }
  }, [cart]);

  // Initialize Square payment form
  useEffect(() => {
    if (squareLoaded && applicationId && squareEnvironment && window.Square && cardRef.current) {
      const initializeSquare = async () => {
        try {
          // Use the environment from server to match the application ID
          const payments = window.Square.payments(applicationId, squareEnvironment);
          
          // Handle both Promise and direct return cases
          let paymentsInstance;
          if (payments && typeof payments.then === 'function') {
            // It's a Promise
            paymentsInstance = await payments;
          } else {
            // It's already the payments instance
            paymentsInstance = payments;
          }

          try {
            const card = await paymentsInstance.card();
            await card.attach(cardRef.current);
            cardRef.current.cardInstance = card;
            paymentsRef.current = paymentsInstance;
          } catch (error) {
            console.error('Failed to attach card form:', error);
            toast.error('Failed to initialize payment form. Please refresh the page.');
          }
        } catch (error) {
          console.error('Error initializing Square:', error);
          // Check for environment mismatch error
          if (error.message && error.message.includes('ApplicationIdEnvironmentMismatchError')) {
            toast.error('Square configuration error: Application ID environment mismatch. Please check your server environment variables (SQUARE_ENVIRONMENT or NODE_ENV).', { duration: 8000 });
          } else {
            toast.error('Failed to initialize payment form. Please refresh the page.');
          }
        }
      };

      initializeSquare();
    }

    return () => {
      // Cleanup
      if (cardRef.current?.cardInstance) {
        cardRef.current.cardInstance.destroy().catch(console.error);
      }
    };
  }, [squareLoaded, applicationId, squareEnvironment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentsRef.current || !cardRef.current?.cardInstance) {
      toast.error('Payment form not ready. Please wait a moment and try again.');
      return;
    }

    if (!cardholderName.trim()) {
      toast.error('Please enter the cardholder name');
      return;
    }

    setLoading(true);

    try {
      // Tokenize the card using Square Web Payments SDK
      const payments = paymentsRef.current;
      const card = cardRef.current.cardInstance;
      
      const tokenResult = await card.tokenize();
      
      if (tokenResult.status === 'OK') {
        // Prepare payment data with tokenized card
        const paymentData = {
          sourceId: tokenResult.token,
          cartItems: cart.items,
          customerEmail: user.email,
          customerName: `${user.firstName} ${user.lastName}`
        };

        // Process payment through Square
        const response = await api.post('/api/square/process-payment', paymentData);
        
        if (response.data.success) {
          const failures = response.data.failures || [];
          if (failures.length > 0) {
            const failedList = failures
              .map((f) => `${f.planType}${f.url ? ` (${f.url})` : ''}`)
              .join(', ');
            toast.error(
              `Some items could not be set up: ${failedList}. Please contact support.`,
              { duration: 8000 }
            );
          } else {
            toast.success('Payment successful! Your hosting plans are being set up with auto-recurring payments.');
          }
          
          // Clear cart after successful payment
          await api.delete('/api/cart/clear');
          
          // Notify navbar of cart update
          window.dispatchEvent(new CustomEvent('cartUpdated'));
          
          navigate('/dashboard');
        } else {
          throw new Error(response.data.error || 'Payment failed');
        }
      } else {
        const errorMessage = tokenResult.errors?.[0]?.detail || 'Failed to tokenize card';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Payment failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getPlanFeatures = (planType) => {
    const features = {
      shared: [
        '5GB SSD Storage',
        'Unlimited Bandwidth',
        'Free SSL Certificate',
        'Daily Backups',
        'cPanel Control Panel',
        '99.9% Uptime Guarantee'
      ],
      private: [
        '20GB SSD Storage',
        'Unlimited Bandwidth',
        'Free SSL Certificate',
        'Daily Backups',
        'cPanel Control Panel',
        '99.9% Uptime Guarantee',
        'Malware Scanning',
        'SSH Access'
      ],
      premium: [
        '50GB SSD Storage',
        'Unlimited Bandwidth',
        'Free SSL Certificate',
        'Daily Backups',
        'cPanel Control Panel',
        '99.9% Uptime Guarantee',
        'Malware Scanning',
        'SSH Access',
        'Staging Environment',
        'CDN Setup',
        'Performance Monitoring',
        'Phone Support'
      ]
    };
    return features[planType] || [];
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'shared':
        return '🏠';
      case 'private':
        return '🏢';
      case 'premium':
        return '⭐';
      default:
        return '🌐';
    }
  };

  if (!cart) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const total = location.state?.total || 0;
  const savings = location.state?.savings || 0;

  return (
    <>
      <Helmet>
        <title>Checkout - The Host Bros</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/cart')}
              className="flex items-center text-primary-600 hover:text-primary-700 mb-4"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Cart
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Complete Your Purchase</h1>
            <p className="text-gray-600 mt-2">
              Secure checkout powered by Square
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Information</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Square Payment Form */}
                  {!squareLoaded && !applicationId && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-yellow-900 mb-2">Payment System Configuration Required</h3>
                      <p className="text-sm text-yellow-700">
                        The Square payment system is not configured. Please contact support or check that SQUARE_APPLICATION_ID is set in the server environment variables.
                      </p>
                    </div>
                  )}

                  {!squareLoaded && applicationId && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="text-sm text-gray-600 mt-2">Loading secure payment form...</p>
                    </div>
                  )}

                  {squareLoaded && applicationId && (
                    <>
                      {/* Cardholder Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cardholder Name
                        </label>
                        <input
                          type="text"
                          value={cardholderName}
                          onChange={(e) => setCardholderName(e.target.value)}
                          className="input-field"
                          placeholder="John Doe"
                          required
                        />
                      </div>

                      {/* Square Card Form */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Card Information
                        </label>
                        <div 
                          id="card-container" 
                          ref={cardRef}
                          className="border border-gray-300 rounded-md p-3 min-h-[50px]"
                        ></div>
                        <p className="text-xs text-gray-500 mt-1">
                          Your card information is securely processed by Square
                        </p>
                      </div>
                    </>
                  )}

                  {/* Security Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <LockClosedIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                      <div>
                        <h3 className="text-sm font-medium text-blue-900">Secure Payment</h3>
                        <p className="text-sm text-blue-700 mt-1">
                          Your payment information is encrypted and secure. We use Square for all payment processing.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex justify-center items-center"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <LockClosedIcon className="h-5 w-5 mr-2" />
                        Complete Purchase
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>
                
                {/* Cart Items */}
                <div className="space-y-4 mb-6">
                  {cart.items.map((item) => (
                    <div key={item._id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <span className="text-xl mr-2">{getPlanIcon(item.planType)}</span>
                          <div>
                            <h3 className="font-medium text-gray-900 capitalize">
                              {item.planType} Hosting
                            </h3>
                            <p className="text-sm text-gray-600 capitalize">
                              {item.billingCycle} billing
                            </p>
                          </div>
                        </div>
                        <span className="font-medium">${item.price}</span>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        <p className="break-all"><strong>URL:</strong> {item.url}</p>
                      </div>
                      
                      <div className="text-xs text-gray-500">
                        {getPlanFeatures(item.planType).slice(0, 3).join(' • ')}
                      </div>
                    </div>
                  ))}
                </div>
                
                {savings > 0 && (
                  <div className="flex justify-between text-green-600 mb-4">
                    <span>Yearly Savings</span>
                    <span>-${savings}</span>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-gray-900">${total}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {cart.items.length} hosting plan{cart.items.length !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* What's included */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">What's included:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Free SSL certificates</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600">Daily backups</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600">99.9% uptime guarantee</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-600">24/7 support</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPage; 